import type { JSX } from 'react';

interface SkeletonSectionProps {
  /** Both sections now have their own time-filter dropdown next to their heading. */
  hasFilter?: boolean;
  cardCount: number;
}

function SkeletonSection({ cardCount, hasFilter = false }: SkeletonSectionProps): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="h-6 w-52 rounded bg-surface-elevated" />
        {hasFilter && <div className="h-8 w-40 rounded bg-surface-elevated" />}
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: cardCount }, (_, index) => (
          <div
            key={index}
            className="flex h-full flex-col gap-3 rounded-lg border border-default px-5 py-4"
          >
            {/* product name */}
            <div className="h-3 w-20 rounded bg-surface-elevated" />
            {/* status icon + title + time-ago */}
            <div className="h-4 w-3/4 rounded bg-surface-elevated" />
            {/* task progress bar */}
            <div className="h-2 w-full rounded-full bg-foreground/10" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Loading placeholder for /dashboard, shaped like both real sections (a
 * heading bar plus a select-shaped bar for each section's time filter,
 * followed by a card grid of 3-line card-shaped placeholders, matching the
 * real `ProjectCardGrid`'s breakpoints) so its dimensions approximate the
 * loaded content and avoid layout shift once data arrives. Card counts
 * (6 / 4) approximate ~2 rows / ~1 row of real cards at the largest
 * breakpoint.
 *
 * Always renders both sections fully expanded, regardless of any persisted
 * collapsed state: this component has no access to (and must not read)
 * `localStorage` before the page component mounts, and guessing wrong would
 * itself cause a layout jump when the real, possibly-narrower collapsed
 * content swaps in.
 *
 * Deliberately renders no `<h1>`/title placeholder: `Dashboard.tsx`'s real
 * `<h1>` renders unconditionally, including while this skeleton is showing,
 * so a skeleton title bar would double up with it.
 *
 * Pattern: docs/design-patterns/frontend/ui-component-states.md (loading-indicator-type — skeleton for an initial fetch)
 * Pattern: docs/design-patterns/frontend/frontend-performance.md (reserve-space-for-async-content)
 */
export function DashboardSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" className="animate-pulse space-y-8" role="status">
      <span className="sr-only">Loading dashboard</span>
      <div aria-hidden="true">
        <SkeletonSection cardCount={6} hasFilter />
      </div>
      <div aria-hidden="true">
        <SkeletonSection cardCount={4} hasFilter />
      </div>
    </div>
  );
}
